import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Document, Page } from 'react-pdf';

export function PdfViewerDialog ({
  open,
  onClose,
  pdfName,
  currentPdfLink,
  pageNumber,
  setPageNumber,
  numPages,
  setNumPages,
  selectedAttachment,
  handleEditButton
}) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        sx: {
          width: '90vw',
          maxWidth: '85vw',
          maxHeight: '95vh',
          m: 0,
          borderRadius: '8px'
        }
      }}
    >
      <DialogTitle
        style={{
          color: '#000000',
          padding: '12px 16px',
          background: 'transparent',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 500,
          fontSize: '14px'
        }}
      >
        <span style={{ fontWeight: 500, fontSize: '16px' }}>{pdfName}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {selectedAttachment?.is_application && (
            <Button
              variant='outline'
              size='sm'
              onClick={handleEditButton}
              style={{
                fontSize: '12px',
                padding: '2px 8px',
                height: '26px',
                borderColor: 'rgba(0,0,0,0.1)'
              }}
            >
              <PictureAsPdfIcon
                style={{ fontSize: '14px', marginRight: '4px' }}
              />
              Edit PDF
            </Button>
          )}
          <Button
            onClick={onClose}
            variant='ghost'
            size='sm'
            style={{ padding: '4px', minWidth: 'auto', lineHeight: 1 }}
          >
            <CloseIcon fontSize='small' />
          </Button>
        </div>
      </DialogTitle>
      <DialogContent
        style={{
          padding: '0',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: 'auto',
          maxHeight: '85vh'
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: '#f8fafc',
            borderRadius: 0,
            boxShadow: 'none',
            overflow: 'hidden',
            position: 'relative'
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {!numPages && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  background: 'rgba(255,255,255,0.9)',
                  zIndex: 10,
                  backdropFilter: 'blur(3px)'
                }}
              >
                <CircularProgress
                  size={60}
                  thickness={4}
                  sx={{
                    color: '#ea580c',
                    marginBottom: 2
                  }}
                />
                <Box
                  sx={{
                    mt: 2,
                    fontSize: '16px',
                    color: '#475569',
                    fontWeight: 500
                  }}
                >
                  Loading your document...
                </Box>
              </Box>
            )}
            {/* Sidebar Thumbnails */}
            <div
              style={{
                width: 100,
                minWidth: 100,
                background: '#f3f4f6',
                borderRight: '1px solid #e5e7eb',
                overflowY: 'auto',
                padding: '8px 0'
              }}
            >
              {numPages && (
                <Document
                  file={{ url: currentPdfLink }}
                  loading={null}
                  onLoadSuccess={() => {}}
                  options={{
                    cMapUrl:
                      'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/cmaps/',
                    cMapPacked: true,
                    standardFontDataUrl:
                      'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/standard_fonts/',
                    useSystemFonts: true,
                    isEvalSupported: true,
                    useWorkerFetch: true,
                    renderInteractiveForms: false,
                    disableAutoFetch: true,
                    disableStream: true,
                    disableFontFace: false
                  }}
                >
                  {Array.from(new Array(numPages), (el, idx) => (
                    <div
                      key={idx}
                      style={{
                        margin: '8px',
                        borderRadius: 8,
                        border:
                          pageNumber === idx + 1
                            ? '2px solid #ea580c'
                            : '1px solid #e5e7eb',
                        boxShadow:
                          pageNumber === idx + 1
                            ? '0 0 0 2px rgba(234, 88, 12, 0.2)'
                            : '0 1px 3px rgba(0, 0, 0, 0.1)',
                        cursor: 'pointer',
                        background: pageNumber === idx + 1 ? '#fff7ed' : '#fff',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                          transform: 'translateY(-1px)'
                        }
                      }}
                      onClick={() => setPageNumber(idx + 1)}
                    >
                      <Page
                        pageNumber={idx + 1}
                        width={90}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        loading={null}
                        scale={2}
                        devicePixelRatio={2}
                        className='thumbnail-page'
                      />
                      <div
                        style={{
                          textAlign: 'center',
                          fontSize: 12,
                          color: pageNumber === idx + 1 ? '#ea580c' : '#64748b',
                          fontWeight: pageNumber === idx + 1 ? 600 : 500,
                          padding: '4px 0',
                          backgroundColor:
                            pageNumber === idx + 1 ? '#fff7ed' : 'transparent'
                        }}
                      >
                        {idx + 1}
                      </div>
                    </div>
                  ))}
                </Document>
              )}
            </div>
            {/* Main PDF Page */}
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                overflow: 'auto',
                background: '#fff',
                position: 'relative',
                width: '100%',
                minWidth: 0,
                minHeight: '400px'
              }}
            >
              {!numPages && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    background: 'rgba(255,255,255,0.9)',
                    zIndex: 10
                  }}
                >
                  <div style={{ marginBottom: '16px' }}>
                    <CircularProgress
                      size={48}
                      thickness={4}
                      sx={{
                        color: '#ea580c',
                        animation: 'pulse 1.5s ease-in-out infinite'
                      }}
                    />
                  </div>
                  <p
                    style={{
                      fontSize: '14px',
                      color: '#475569',
                      fontWeight: 500
                    }}
                  >
                    Loading your document...
                  </p>
                  <style>{`
                    @keyframes pulse {
                      0% {
                        transform: scale(0.95);
                        opacity: 0.7;
                      }
                      50% {
                        transform: scale(1.05);
                        opacity: 1;
                      }
                      100% {
                        transform: scale(0.95);
                        opacity: 0.7;
                      }
                    }
                  `}</style>
                </div>
              )}

              <div
                style={{
                  position: 'relative',
                  flex: '1',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                  overflowY: 'auto',
                  padding: '8px 0'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '90%'
                  }}
                >
                  <Document
                    file={{ url: currentPdfLink }}
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    className='pdf-doc-wrapper'
                    loading={null}
                    options={{
                      cMapUrl:
                        'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/cmaps/',
                      cMapPacked: true,
                      standardFontDataUrl:
                        'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/standard_fonts/',
                      useSystemFonts: true,
                      isEvalSupported: true,
                      useWorkerFetch: true,
                      renderInteractiveForms: true,
                      disableAutoFetch: true,
                      disableStream: true,
                      disableFontFace: false
                    }}
                  >
                    <Page
                      pageNumber={pageNumber}
                      width={800}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      scale={2}
                      devicePixelRatio={2}
                      loading={null}
                      canvasBackground='#ffffff'
                      className='pdf-page'
                      error={error => (
                        <div className='text-red-500'>
                          Error loading page: {error.message}
                        </div>
                      )}
                    />
                  </Document>
                </div>
              </div>
            </div>
          </Box>

          {/* Fixed Bottom Pagination Controls */}
          {numPages > 0 && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px 12px',
                height: '36px',
                minHeight: '36px',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                background: '#fff',
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
                boxShadow: '0 -2px 4px rgba(0,0,0,0.02)'
              }}
            >
              <Button
                variant='contained'
                size='small'
                onClick={() => setPageNumber(p => Math.max(p - 1, 1))}
                disabled={pageNumber <= 1}
                sx={{
                  minWidth: '32px',
                  height: '28px',
                  padding: '0px 4px',
                  fontSize: '13px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#e5e7eb',
                    boxShadow: 'none'
                  },
                  '&:disabled': {
                    backgroundColor: '#f9fafb',
                    color: '#d1d5db'
                  }
                }}
              >
                ←
              </Button>
              <span
                style={{
                  fontWeight: 500,
                  fontSize: 13,
                  margin: '0 8px',
                  lineHeight: '28px'
                }}
              >
                Page{' '}
                <input
                  type='number'
                  min={1}
                  max={numPages || 1}
                  value={pageNumber}
                  onChange={e => {
                    let val = Number(e.target.value);
                    if (val > numPages) val = numPages;
                    if (val < 1) val = 1;
                    setPageNumber(val);
                  }}
                  style={{
                    width: 30,
                    height: '22px',
                    textAlign: 'center',
                    border: '1px solid #e5e7eb',
                    borderRadius: 3,
                    margin: '0 4px',
                    fontSize: 12,
                    padding: '0px 2px',
                    lineHeight: '22px'
                  }}
                />{' '}
                of {numPages || 1}
              </span>
              <Button
                variant='contained'
                size='small'
                onClick={() =>
                  setPageNumber(p => Math.min(p + 1, numPages || 1))
                }
                disabled={pageNumber >= (numPages || 1)}
                sx={{
                  minWidth: '32px',
                  height: '28px',
                  padding: '0px 4px',
                  fontSize: '13px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #e5e7eb',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#e5e7eb',
                    boxShadow: 'none'
                  },
                  '&:disabled': {
                    backgroundColor: '#f9fafb',
                    color: '#d1d5db'
                  }
                }}
              >
                →
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>{' '}
      <style>{`
        .pdf-page {
          canvas {
            max-width: 100%;
            height: auto !important;
            background-color: white !important;
            image-rendering: high-quality;
            -webkit-font-smoothing: antialiased;
            filter: brightness(1.02) contrast(1.05);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
            border-radius: 4px;
          }
        }
        .pdf-doc-wrapper {
          display: flex;
          justify-content: center;
          width: 100%;
          background-color: white;
          padding: 16px;
        }
        .thumbnail-page {
          canvas {
            width: 100% !important;
            height: auto !important;
            image-rendering: high-quality;
            -webkit-font-smoothing: antialiased;
            filter: brightness(1.02) contrast(1.05);
          }
        }
      `}</style>
    </Dialog>
  );
}
